import { getWsBase } from "./config";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { telecomDispatchActions, powerGasDispatchActions, jointClaimsQueueActions } from "_actions";
import WebSocket from "isomorphic-ws";

export let client = null;
let heartbeatInterval = null;
let reconnectTimeout = null;
let websocketInitialized = false;

const callbacks = {
   confirmDispatch: null,
   attachmentColumns: null,
   recoveryAgents: null,
   claimAssistants: null,
   roundRobinBuckets: null,
   recoveryAgentsForBucket: {},
   claimAssistantsForBucket: {},
   recoveryTeams: null,
   recoveryAgentsForTeam: {},
   agentStats: {},
   assistantStats: {},
   preLegalClaims: null,
   processLegalClaims: null,
   managementTeams: null,
   internalActiveUsers: null,
   entityLocateComapnies: null,
   roundRobinHistory: null,
   legacyEntityLocateComapnies: null,
   scoreboardMarkets: null,
   scoreboardRegions: null,
   scoreboardEntities: null,
   nextUp: null,
   distributionSettings: null,
   getRecoveryRegions: null,
   teamsForBucket: {},
   refreshBucket: null,
   getRoundRobinAssignments: null,
   claimEditors: {}
}

const serialize = (data) => {
  return JSON.stringify(data);
};

const clearHeartbeatInterval = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

const startHeartbeatInterval = () => {
  clearHeartbeatInterval();
  heartbeatInterval = setInterval(() => {
    if (client?.readyState === WebSocket.OPEN && client.lastMessage !== undefined && Date.now() - client.lastMessage > 15000) {
      client.close();
    }
  }, 15000);
};

const ensureClient = () => {
  if (client === null || client.readyState === WebSocket.CLOSED || client.readyState === WebSocket.CLOSING) {
    client = new WebSocket(getWsBase());
    websocketInitialized = false;
  }

  return client;
};

const isClientOpen = () => client?.readyState === WebSocket.OPEN;

export const UseWebSocket = () => {
  const dispatch = useDispatch();

  const initWebSocket = useCallback(() => {
    const activeClient = ensureClient();

    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    if (
      websocketInitialized &&
      (activeClient.readyState === WebSocket.OPEN || activeClient.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    websocketInitialized = true;

    activeClient.onopen = () => {
      console.log("opened socket");
      startHeartbeatInterval();
    };

    activeClient.onmessage = async (message) => {
      const deserialized = JSON.parse(message.data);
      client.lastMessage = Date.now();
      switch (deserialized.type) {
        case "whois":
          // Example: Read a specific cookie by name
          function getCookie(name) {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(";").shift();
          }

          const token = getCookie("token");
          const assumingRole = getCookie("assumingRole");
          if (token === null || token === undefined) {
            return;
          }
          client.send(serialize({ type: "whois", message: token, assumingRole: assumingRole === "true" }));
          break;
        case "getDispatch":
          if (deserialized.db === "dbClaim") {
            dispatch(telecomDispatchActions.updateDispatchTimer(deserialized.timers));
            dispatch(telecomDispatchActions.updateDispatches(deserialized.dispatches));
          }
          if (deserialized.db === "dbPowGas") {
            dispatch(powerGasDispatchActions.updateDispatchTimer(deserialized.timers));
            dispatch(powerGasDispatchActions.updateDispatches(deserialized.dispatches));
          }
          break;
        case "updateColor":
          if (deserialized.db === "dbClaim")
            dispatch(
              telecomDispatchActions.applyDispatchTimerColor({
                lobid: 1,
                claimId: deserialized.claimId,
                color: deserialized.color,
              })
            );
          else if (deserialized.db === "dbPowGas")
            dispatch(
              powerGasDispatchActions.applyDispatchTimerColor({
                lobid: 2,
                claimId: deserialized.claimId,
                color: deserialized.color,
              })
            );
          break;
        case "updatePaused":
          if (deserialized.db === "dbClaim")
            dispatch(
              telecomDispatchActions.applyDispatchTimerPause({
                lobid: 1,
                claimId: deserialized.claimId,
                pause: deserialized.paused,
              })
            );
          else if (deserialized.db === "dbPowGas")
            dispatch(
              powerGasDispatchActions.applyDispatchTimerPause({
                lobid: 2,
                claimId: deserialized.claimId,
                pause: deserialized.paused,
              })
            );
          break;
        case "claimsQueue":
          dispatch(jointClaimsQueueActions.updateClaimsQueue(deserialized.queues, deserialized.roleId));
          break;
        case "dispatchConfirmed":
          dispatch(telecomDispatchActions.confirmDispatch(deserialized.claimId));
          if (callbacks.confirmDispatch) {
            callbacks.confirmDispatch();
            callbacks.confirmDispatch = null;
          }
          break;
        case "getAttachmentColumns":
          if (callbacks.attachmentColumns) {
            callbacks.attachmentColumns(deserialized.columns);
            callbacks.attachmentColumns = null;
          }
          break;
        case "getRecoveryAgents":
          if (callbacks.recoveryAgents) {
            callbacks.recoveryAgents(deserialized.agents);
            callbacks.recoveryAgents = null;
          }
          break;
        case "getClaimAssistants":
          if (callbacks.claimAssistants) {
            callbacks.claimAssistants(deserialized.agents);
            callbacks.claimAssistants = null;
          }
          break;
        case "getRoundRobinBuckets":
          if (callbacks.roundRobinBuckets) {
            callbacks.roundRobinBuckets(deserialized.buckets);
            callbacks.roundRobinBuckets = null;
          }
          break;
        case "getRecoveryAgentsForBucket":
          if (callbacks.recoveryAgentsForBucket[deserialized.bucket_node_id]) {
            callbacks.recoveryAgentsForBucket[deserialized.bucket_node_id](deserialized.agents);
            callbacks.recoveryAgentsForBucket[deserialized.bucket_node_id] = null;
          }
          break;
        case "getTeamsForBucket":
          if (callbacks.teamsForBucket[deserialized.bucket_node_id]) {
            callbacks.teamsForBucket[deserialized.bucket_node_id](deserialized.teams);
            callbacks.teamsForBucket[deserialized.bucket_node_id] = null;
          }
          break;
        case "getClaimAssistantsForBucket":
          if (callbacks.claimAssistantsForBucket[deserialized.bucket_node_id]) {
            callbacks.claimAssistantsForBucket[deserialized.bucket_node_id](deserialized.agents);
            callbacks.claimAssistantsForBucket[deserialized.bucket_node_id] = null;
          }
          break;
        case "getRecoveryTeams":
          if (callbacks.recoveryTeams) {
            callbacks.recoveryTeams(deserialized.teams);
            callbacks.recoveryTeams = null;
          }
          break;
        case "getRecoveryAgentsForTeam":
          if (callbacks.recoveryAgentsForTeam[deserialized.team_node_id]) {
            callbacks.recoveryAgentsForTeam[deserialized.team_node_id](deserialized.agents);
            callbacks.recoveryAgentsForTeam[deserialized.team_node_id] = null;
          }
          break;
        case "getStatsForAgent":
          if (callbacks.agentStats[deserialized.agent_node_id]) {
            callbacks.agentStats[deserialized.agent_node_id](deserialized.stats);
            callbacks.agentStats[deserialized.agent_node_id] = null;
          }
          break;
        case "getStatsForAssistant":
          if (callbacks.assistantStats[deserialized.agent_node_id]) {
            callbacks.assistantStats[deserialized.agent_node_id](deserialized.stats);
            callbacks.assistantStats[deserialized.agent_node_id] = null;
          }
          break;
        case "getPreLegalClaims":
          if (callbacks.preLegalClaims) {
            callbacks.preLegalClaims(deserialized.claims);
            callbacks.preLegalClaims = null;
          }
          break;
        case "processLegalClaims":
          if (callbacks.processLegalClaims) {
            callbacks.processLegalClaims(deserialized.claims);
            callbacks.processLegalClaims = null;
          }
          break;
        case "getManagementTeams":
          if (callbacks.managementTeams) {
            callbacks.managementTeams(deserialized.teams);
            callbacks.managementTeams = null;
          }
          break;
        case "getInternalActiveUsers":
          if (callbacks.internalActiveUsers) {
            callbacks.internalActiveUsers(deserialized.users);
            callbacks.internalActiveUsers = null;
          }
          break;
        case "getEntityLocateCompanies":
          if (callbacks.entityLocateComapnies) {
            callbacks.entityLocateComapnies(deserialized.results);
            callbacks.entityLocateComapnies = null;
          }
          break;
        case "getRoundRobinHistory":
          if (callbacks.roundRobinHistory) {
            callbacks.roundRobinHistory(deserialized.history);
            callbacks.roundRobinHistory = null;
          }
          break;
        case "getLegacyEntityLocateCompanies":
          if (callbacks.legacyEntityLocateComapnies) {
            callbacks.legacyEntityLocateComapnies(deserialized.results);
            callbacks.legacyEntityLocateComapnies = null;
          }
          break;
        case "getScoreboardMarkets":
          if (callbacks.scoreboardMarkets) {
            callbacks.scoreboardMarkets(deserialized.markets);
            callbacks.scoreboardMarkets = null;
          }
          break;
        case "getScoreboardRegions":
          if (callbacks.scoreboardRegions) {
            callbacks.scoreboardRegions(deserialized.regions);
            callbacks.scoreboardRegions = null;
          }
          break;
        case "getScoreboardEntities":
          if (callbacks.scoreboardEntities) {
            callbacks.scoreboardEntities(deserialized.entities);
            callbacks.scoreboardEntities = null;
          }
          break;
        case "getNextUp":
          if (callbacks.nextUp) {
            callbacks.nextUp(deserialized.nextUp);
            callbacks.nextUp = null;
          }
          break;
        case "getRRDistributionSettings":
          if (callbacks.distributionSettings) {
            callbacks.distributionSettings(deserialized.settings);
            callbacks.distributionSettings = null;
          }
          break;
        case "getRecoveryRegions":
          if (callbacks.getRecoveryRegions) {
            callbacks.getRecoveryRegions(deserialized.regions);
            callbacks.getRecoveryRegions = null;
          }
          break;
        case "updateRoundRobinTeamSettings":
          if (callbacks.refreshBucket) {
            callbacks.refreshBucket();
            callbacks.refreshBucket = null;
          }
          break;
        case "getRoundRobinAssignments":
          if (callbacks.getRoundRobinAssignments) {
            callbacks.getRoundRobinAssignments(deserialized.results);
            callbacks.getRoundRobinAssignments = null;
          }
          break;
        case "getClaimEditors":
          if (callbacks.claimEditors[deserialized.claimId]) {
            callbacks.claimEditors[deserialized.claimId](deserialized.editors || []);
          }
          break;
      }
    };

    activeClient.onclose = (reason) => {
      websocketInitialized = false;
      clearHeartbeatInterval();
      if (process.env.REACT_APP_API === "apptest" || process.env.REACT_APP_API === "dev")
        console.log("closed socket", reason);

      reconnectTimeout = setTimeout(() => {
        reconnectTimeout = null;
        initWebSocket();
      }, 1000);
    };

    if (activeClient.readyState === WebSocket.OPEN) {
      startHeartbeatInterval();
    }
  }, [dispatch]);

  const getClaimsQueue = () => {
    if (isClientOpen()) client.send(serialize({ type: "claimsQueue" }));
  };

  const getClaimEditors = (claimId, setEditors) => {
    callbacks.claimEditors[claimId] = setEditors;
    if (isClientOpen()) client.send(serialize({ type: "getClaimEditors", claimId }));
  };

  const clearClaimEditors = (claimId) => {
    delete callbacks.claimEditors[claimId];
  };

  const markClaimEdited = (claimId, sessionId) => {
    if (isClientOpen()) client.send(serialize({ type: "markClaimEdited", claimId, sessionId }));
  };

  const heartbeatClaimEditing = (claimId, sessionId) => {
    if (isClientOpen()) client.send(serialize({ type: "heartbeatClaimEditing", claimId, sessionId }));
  };

  const removeClaimEditing = (claimId, sessionId) => {
    if (isClientOpen()) client.send(serialize({ type: "removeClaimEditing", claimId, sessionId }));
  };

  const saveStatusNoteHighlight = (id, Hlight, LOBID) => {
    if (isClientOpen()) client.send(serialize({ type: "statusNoteHlight", Hlight, id, LOBID }));
  };

  // Currently telecom only
  const confirmDispatch = (claimId, cb) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "confirmDispatch", claimId }));
      callbacks.confirmDispatch = cb;
    }
  };

  const saveAttachmentColumns = (columns) => {
    if (isClientOpen()) client.send(serialize({ type: "saveAttachmentColumns", columns }));
  };

  const getAttachmentColumns = (setColumnFilters) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getAttachmentColumns" }));
      callbacks.attachmentColumns = (columns) => {
        const filterArr = Object.keys(columns).map((key) => {
          return { [key]: columns[key] };
        });
        setColumnFilters(filterArr);
      };
    }
  };

  const getRecoveryAgents = (setAgents, locateOnly = false) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getRecoveryAgents", locateOnly }));
      callbacks.recoveryAgents = (agents) => {
        setAgents(agents);
      };
    }
  };
  const getClaimAssistants = (setAgents) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getClaimAssistants" }));
      callbacks.claimAssistants = (agents) => {
        setAgents(agents);
      };
    }
  };

  const getRoundRobinBuckets = (setBuckets) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getRoundRobinBuckets" }));
      callbacks.roundRobinBuckets = (buckets) => {
        setBuckets(buckets);
      };
    }
  };

  const addAgentToBucket = (agent_node_id, bucket_node_id, setBucketAgents, subType) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "addAgentToBucket", agent_node_id, bucket_node_id, subType }));
      if (subType === "recovery")
        callbacks.recoveryAgentsForBucket[bucket_node_id] = (agents) => {
          setBucketAgents((buckets) => {
            return { ...buckets, [`${bucket_node_id}`]: agents };
          });
        };
      if (subType === "claims")
        callbacks.claimAssistantsForBucket[bucket_node_id] = (agents) => {
          setBucketAgents((buckets) => {
            return { ...buckets, [`${bucket_node_id}`]: agents };
          });
        };
    }
  };

  const removeAgentFromBucket = (edgeId, bucket_node_id, setBucketAgents, subType) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "removeAgentFromBucket", edgeId, bucket_node_id, subType }));
      if (subType === "recovery")
        callbacks.recoveryAgentsForBucket[bucket_node_id] = (agents) => {
          setBucketAgents((buckets) => {
            return { ...buckets, [`${bucket_node_id}`]: agents };
          });
        };
      if (subType === "claims")
        callbacks.claimAssistantsForBucket[bucket_node_id] = (agents) => {
          setBucketAgents((buckets) => {
            return { ...buckets, [`${bucket_node_id}`]: agents };
          });
        };
    }
  };

  const getRecoveryAgentsForBucket = (bucket_node_id, setBucketAgents, resolve) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getRecoveryAgentsForBucket", bucket_node_id }));
      callbacks.recoveryAgentsForBucket[bucket_node_id] = (agents) => {
        setBucketAgents((buckets) => {
          return { ...buckets, [bucket_node_id]: agents || [] };
        });
        resolve();
      };
    }
  };

  const addTeamToBucket = (team_node_id, bucket_node_id, setBucketTeams) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "addTeamToBucket", team_node_id, bucket_node_id }));
        callbacks.teamsForBucket[bucket_node_id] = (teams) => {
          setBucketTeams((buckets) => {
            return { ...buckets, [`${bucket_node_id}`]: teams };
          });
        };
    }
  };

  const removeTeamFromBucket = (edgeId, bucket_node_id, setBucketTeams) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "removeTeamFromBucket", edgeId, bucket_node_id }));
        callbacks.teamsForBucket[bucket_node_id] = (teams) => {
          setBucketTeams((buckets) => {
            return { ...buckets, [`${bucket_node_id}`]: teams };
          });
        };
    }
  };

  const getTeamsForBucket = (bucket_node_id, setBucketTeams, resolve, withQueues) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getTeamsForBucket", bucket_node_id, withQueues }));
      callbacks.teamsForBucket[bucket_node_id] = (teams) => {
        setBucketTeams((buckets) => {
          return { ...buckets, [bucket_node_id]: teams || [] };
        });
        if (resolve) resolve();
      };
    }
  };

  const getClaimAssistantsForBucket = (bucket_node_id, setBucketAgents, resolve) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getClaimAssistantsForBucket", bucket_node_id }));
      callbacks.claimAssistantsForBucket[bucket_node_id] = (agents) => {
        setBucketAgents((buckets) => {
          return { ...buckets, [bucket_node_id]: agents || [] };
        });
        resolve();
      };
    }
  };

  const updateRoundRobinSettings = (edgeId, settings) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "updateRoundRobinSettings", edgeId, settings }));
    }
  };

  const updateRoundRobinTeamSettings = (edgeId, settings, refreshBucket) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "updateRoundRobinTeamSettings", edgeId, settings }));
      callbacks.refreshBucket = refreshBucket;
    }
  };

  const getRecoveryTeams = (setTeams) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getRecoveryTeams" }));
      callbacks.recoveryTeams = (teams) => {
        setTeams(teams);
      };
    }
  };

  const getRecoveryAgentsForTeam = (team_node_id, setTeamAgents, resolve) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getRecoveryAgentsForTeam", team_node_id }));
      callbacks.recoveryAgentsForTeam[team_node_id] = (agents) => {
        setTeamAgents((teams) => {
          return { ...teams, [team_node_id]: agents || [] };
        });
        resolve();
      };
    }
  };

  const addAgentToTeam = (agent_node_id, team_node_id, startDate, setTeamAgents, cb) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "addAgentToTeam", agent_node_id, team_node_id, startDate }));
      callbacks.recoveryAgentsForTeam[team_node_id] = (agents) => {
        setTeamAgents((teams) => {
          return { ...teams, [team_node_id]: agents };
        });
        if (cb) cb();
      };
    }
  };

  const removeAgentFromTeam = (edgeId, team_node_id, endDate, setTeamAgents, cb) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "removeAgentFromTeam", edgeId, team_node_id, endDate }));
      callbacks.recoveryAgentsForTeam[team_node_id] = (agents) => {
        setTeamAgents((teams) => {
          return { ...teams, [team_node_id]: agents };
        });
        if (cb) cb();
      };
    }
  };

  const getStatsForAgent = (agent_node_id, setStats) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getStatsForAgent", agent_node_id }));
      callbacks.agentStats[agent_node_id] = (stats) => {
        setStats(stats);
      };
    }
  };

  const getStatsForAssistant = (agent_node_id, setStats) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getStatsForAssistant", agent_node_id }));
      callbacks.assistantStats[agent_node_id] = (stats) => {
        setStats(stats);
      };
    }
  };

  const updateTeamName = (value, team_node_id) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "updateTeamName", value, team_node_id }));
    }
  };

  const updateTeamCode = (value, team_node_id) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "updateTeamCode", value, team_node_id }));
    }
  };

  const addNewTeam = (setTeams, cb, TeamName, TeamCode) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "addNewTeam", TeamName, TeamCode }));
      callbacks.recoveryTeams = (teams) => {
        cb();
        setTeams(teams);
      };
    }
  };

  const getPreLegalClaims = (setClaims, cb = null) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getPreLegalClaims" }));
      callbacks.preLegalClaims = (claims) => {
        setClaims(claims);
        if (cb) cb();
      };
    }
  };

  const processLegalClaims = (TelClaims, PgClaims, cb = null) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "processLegalClaims", TelClaims, PgClaims }));
      callbacks.processLegalClaims = (claims) => {
        if (cb) cb();
      };
    }
  };

  const getManagementTeams = (setTeams) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getManagementTeams" }));
      callbacks.managementTeams = (teams) => {
        setTeams(teams);
      };
    }
  };

  const getInternalActiveUsers = (setUsers) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getInternalActiveUsers" }));
      callbacks.internalActiveUsers = (users) => {
        setUsers(users);
      };
    }
  };

  const addMemberToManagementTeam = (memberNodeId, managerNodeId, setTeams) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "addMemberToManagementTeam", memberNodeId, managerNodeId }));
      callbacks.managementTeams = (teams) => {
        setTeams(teams);
      };
    }
  };

  const removeMemberFromManagementTeam = (edgeId, setTeams) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "removeMemberFromManagementTeam", edgeId }));
      callbacks.managementTeams = (teams) => {
        setTeams(teams);
      };
    }
  };

  const getEntityLocateCompanies = (setData) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getEntityLocateCompanies" }));
      callbacks.entityLocateComapnies = (data) => {
        setData(data);
      };
    }
  };

  const getLegacyEntityLocateCompanies = (setData) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getLegacyEntityLocateCompanies" }));
      callbacks.legacyEntityLocateComapnies = (data) => {
        setData(data);
      };
    }
  };

  const getRoundRobinHistory = (setHistory, startDate, endDate) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getRoundRobinHistory", startDate, endDate }));
      callbacks.roundRobinHistory = (history) => {
        setHistory(history);
      };
    }
  };

  const getScoreboardMarkets = (setMarkets) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getScoreboardMarkets" }));
      callbacks.scoreboardMarkets = (markets) => {
        setMarkets(markets);
      };
    }
  };

  const updateScoreboardMarkets = (setMarkets, markets) => {
    if (isClientOpen()) {

      client.send(serialize({ type: "updateScoreboardMarkets", markets }));
      callbacks.scoreboardMarkets = (markets) => {
        setMarkets(markets);
      };
    }
  };

  const getScoreboardRegions = (setRegions) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getScoreboardRegions" }));
      callbacks.scoreboardRegions = (regions) => {
        setRegions(regions);
      };
    }
  };

  const addScoreboardMarket = (setMarkets, market) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "addScoreboardMarket", market }));
      callbacks.scoreboardMarkets = (markets) => {
        setMarkets(markets);
      }
    }
  }

  const getScoreboardEntities = (setEntities) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getScoreboardEntities" }));
      callbacks.scoreboardEntities = (entities) => {
        setEntities(entities);
      }
    }
  }

  const getNextUp = (setNextUp, bucket, bucketType, team) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getNextUp", bucket, bucketType, team }));
      callbacks.nextUp = (nextUp) => {
        setNextUp(nextUp);
      }
    }
  }

  const getRRDistributionSettings = (setDistributionSettings, bucketType) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getRRDistributionSettings", bucketType}));
      callbacks.distributionSettings = (settings) => {
        setDistributionSettings(settings);
      }
    }
  }

  const updateRRDistributionSettings = (settings, bucketType) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "updateRRDistributionSettings", settings, bucketType}));
    }
  }

  const getRecoveryRegions = (setRecoveryRegions) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getRecoveryRegions" }));
      callbacks.getRecoveryRegions = (regions) => {
        setRecoveryRegions(regions);
      }
    }
  }

  const setRecoveryRegion = (prevRegion, regionID, recoveryTeamID, setRecoveryRegions) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "setRecoveryRegion", prevRegion, regionID, recoveryTeamID }));
      callbacks.getRecoveryRegions = (regions) => {
        setRecoveryRegions(regions);
      }
    }
  }

  const getRoundRobinAssignments = (startDate, endDate, bucket, setAssignments) => {
    if (isClientOpen()) {
      client.send(serialize({ type: "getRoundRobinAssignments", startDate, endDate, bucket }));
      callbacks.getRoundRobinAssignments = (results) => {
        setAssignments(results);
      }
    }
  }

  return {
    initWebSocket,
    getClaimsQueue,
    getClaimEditors,
    clearClaimEditors,
    markClaimEdited,
    heartbeatClaimEditing,
    removeClaimEditing,
    saveStatusNoteHighlight,
    confirmDispatch,
    saveAttachmentColumns,
    getAttachmentColumns,
    getRecoveryAgents,
    getRoundRobinBuckets,
    addAgentToBucket,
    getRecoveryAgentsForBucket,
    getTeamsForBucket,
    updateRoundRobinSettings,
    removeAgentFromBucket,
    getClaimAssistants,
    getClaimAssistantsForBucket,
    getRecoveryTeams,
    getRecoveryAgentsForTeam,
    addAgentToTeam,
    removeAgentFromTeam,
    getStatsForAgent,
    getStatsForAssistant,
    updateTeamName,
    updateTeamCode,
    addNewTeam,
    getPreLegalClaims,
    processLegalClaims,
    getManagementTeams,
    getInternalActiveUsers,
    addMemberToManagementTeam,
    removeMemberFromManagementTeam,
    getEntityLocateCompanies,
    getRoundRobinHistory,
    getLegacyEntityLocateCompanies,
    getScoreboardMarkets,
    updateScoreboardMarkets,
    getScoreboardRegions,
    addScoreboardMarket,
    getScoreboardEntities,
    getNextUp,
    getRRDistributionSettings,
    updateRRDistributionSettings,
    getRecoveryRegions,
    setRecoveryRegion,
    addTeamToBucket,
    removeTeamFromBucket,
    updateRoundRobinTeamSettings,
    getRoundRobinAssignments,
  };
};
